import { useEffect, useRef, useState } from 'react'

import { styled } from 'styled-components'

import {
  CurrencyStringCents,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import { QuoteRating } from '@b-stock/shipment-api-client'

import { ErrorBannerComponent } from '@/buyer-portal/components/ErrorBanner'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import {
  getCarrierName,
  getServiceLevel,
  groupParcelRates,
} from './groupParcelRates'
import { RateBadge } from './ParcelRateCard'
import { ParcelRateGroup } from './ParcelRateGroup'
import type { ParcelQuote } from './types'

const ns = 'ParcelRateList'

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const Summary = styled.div`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.black};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
`

const BannerWrapper = styled.div`
  margin-top: 0.75rem;
`

export type ParcelRateListProps = {
  quotes: ParcelQuote[]
  currency: string
  /**
   * Persist the buyer's rate choice (reuses the carrier-change mutation). The
   * resolved promise signals success; a rejection triggers a rollback + error.
   */
  onSelectQuote?: (quoteId: string) => Promise<void>
  /** Pending signal from the parent mutation (e.g. `isCarrierChangePending`). */
  isSelectionPending?: boolean
  /** Replaces the error-derived banner text when a selection fails. */
  selectErrorMessage?: string
}

/**
 * Identify the rate the server has marked as selected. We mark the quote with
 * `selected === true`, falling back to the `CHEAPEST`-rated quote, then the
 * first quote shown.
 */
const getSelectedQuoteId = (quotes: ParcelQuote[]): string | undefined => {
  const selected = quotes.find((q) => q.selected === true)
  if (selected) return selected._id

  const cheapest = quotes.find((q) => q.rating === QuoteRating.Cheapest)
  if (cheapest) return cheapest._id

  return groupParcelRates(quotes)[0]?.quotes[0]?._id
}

export const ParcelRateList = ({
  quotes,
  currency,
  onSelectQuote,
  isSelectionPending = false,
  selectErrorMessage,
}: ParcelRateListProps) => {
  const { trackButtonClicked } = useAnalytics()

  const serverSelectedId = getSelectedQuoteId(quotes)

  // Optimistic selection: seeded from the server-selected logic, updated
  // immediately on click and reverted if the mutation fails.
  const [optimisticSelectedId, setOptimisticSelectedId] =
    useState(serverSelectedId)
  // Track our own in-flight boolean so we disable siblings immediately, even
  // before the parent's `isSelectionPending` flips on the next render.
  const [isLocalPending, setIsLocalPending] = useState(false)
  const [selectError, setSelectError] = useState<unknown>(null)
  // Groups the buyer opened or closed. Any other group is open only while it
  // holds the selected rate.
  const [toggledGroups, setToggledGroups] = useState<Record<string, boolean>>(
    {}
  )

  // Adopt the server's selection only when it actually *changes* — e.g. an
  // external update, or the post-mutation refetch landing the new value. We
  // deliberately don't resync on the pending flag flipping: with
  // `placeholderData: keepPreviousData` upstream, the quote set briefly serves
  // the prior (stale) data during the key-churn refetch, and resyncing then
  // would flicker the radio back to the old selection before the new data lands.
  const lastServerSelectedId = useRef(serverSelectedId)
  useEffect(() => {
    if (serverSelectedId !== lastServerSelectedId.current) {
      lastServerSelectedId.current = serverSelectedId
      setOptimisticSelectedId(serverSelectedId)
    }
  }, [serverSelectedId])

  if (quotes.length === 0) return null

  const isPending = isLocalPending || isSelectionPending
  const groups = groupParcelRates(quotes)
  const selectedQuote = quotes.find((q) => q._id === optimisticSelectedId)
  const selectedCarrierName = selectedQuote && getCarrierName(selectedQuote)

  const handleSelect = async (quoteId: string) => {
    // Re-selecting the already-selected rate is a no-op (skip the mutation).
    if (quoteId === optimisticSelectedId || isPending) return

    const previousSelectedId = optimisticSelectedId
    setSelectError(null)
    setOptimisticSelectedId(quoteId)
    trackButtonClicked({ buttonName: 'parcel_rate_selected' })

    if (!onSelectQuote) return

    setIsLocalPending(true)
    try {
      await onSelectQuote(quoteId)
    } catch (error) {
      // Roll back the optimistic selection and surface the failure.
      setOptimisticSelectedId(previousSelectedId)
      setSelectError(error)
    } finally {
      setIsLocalPending(false)
    }
  }

  return (
    <Container data-testid="parcel-rate-list">
      {selectedQuote && (
        <Summary data-testid="parcel-rate-summary">
          <FormattedMessage
            id={`${ns}.summary`}
            values={{
              service: getServiceLevel(selectedQuote) ?? '',
              price: (
                <CurrencyStringCents
                  value={selectedQuote.totalPrice}
                  currency={currency}
                />
              ),
            }}
          />
          {selectedQuote.rating === QuoteRating.Cheapest && (
            <RateBadge>
              <FormattedMessage id={`${ns}.badge.cheapest`} />
            </RateBadge>
          )}
        </Summary>
      )}
      <List>
        {groups.map((group) => {
          const isOpen =
            toggledGroups[group.carrierName] ??
            group.carrierName === selectedCarrierName
          return (
            <ParcelRateGroup
              key={group.carrierName}
              group={group}
              selectedQuoteId={optimisticSelectedId}
              isOpen={isOpen}
              onToggle={() =>
                setToggledGroups((toggled) => ({
                  ...toggled,
                  [group.carrierName]: !isOpen,
                }))
              }
              showCheapestBadge={groups.length > 1}
              currency={currency}
              disabled={isPending}
              onSelect={handleSelect}
            />
          )
        })}
      </List>
      {selectError ? (
        <BannerWrapper>
          <ErrorBannerComponent
            error={selectError}
            errorMessage={selectErrorMessage}
          />
        </BannerWrapper>
      ) : null}
    </Container>
  )
}
